import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BookOpen, Clapperboard, Music2, Play, Users, Youtube } from "lucide-react";
import { SITE } from "@/config/site";
import { Reveal } from "@/components/Reveal";
import { StarOrnament, StarPattern } from "@/components/Ornament";
import { BookButton } from "@/components/GoldButton";
import { StickyCta } from "@/components/StickyCta";
import { trackEvent, trackOutbound, withUtm } from "@/lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SectionHeading({ number, title, urdu, icon: Icon }) {
  return (
    <Reveal>
      <div className="flex items-center gap-4">
        {Icon && <Icon className="h-6 w-6 shrink-0 text-[#D4AF37]" aria-hidden="true" />}
        <span className="font-display text-lg text-[#D4AF37]">{number}</span>
        <span className="h-px flex-1 bg-gradient-to-r from-[#D4AF37]/60 to-transparent" aria-hidden="true" />
      </div>
      <h2 className="font-display mt-5 text-3xl leading-tight tracking-tight text-[#F7F4EF] sm:text-4xl lg:text-5xl">
        {title}
      </h2>
      {urdu && (
        <p dir="rtl" lang="ur" className="font-urdu mt-3 text-2xl leading-relaxed text-[#D4AF37]/75">
          {urdu}
        </p>
      )}
    </Reveal>
  );
}

export default function ArtistPage() {
  const artist = SITE.artist;
  const [playingVideoId, setPlayingVideoId] = useState(null);

  useEffect(() => {
    trackEvent("page_view", { page_path: "/artist" });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#080705] text-[#F7F4EF] antialiased">
      <div className="grain" aria-hidden="true" />

      {/* Header */}
      <header className="relative z-20 border-b border-[#D4AF37]/15">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
          <a href="/" data-testid="artist-home-link" className="flex items-center">
            <img src={SITE.brand.logoSmall} alt={SITE.brand.logoAlt} className="h-12 w-auto" />
          </a>
          <a
            href="/"
            data-testid="artist-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Back to Site
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section
          data-testid="artist-hero-section"
          className="relative overflow-hidden px-5 py-16 sm:px-8 sm:py-24 lg:py-28"
        >
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[#D4AF37]/[0.09] blur-[120px]" />
          </div>

          <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">
                The Artist Behind {SITE.eventName}
              </p>
              <h1 className="font-display mt-4 text-[13vw] leading-[0.98] tracking-tight text-[#F7F4EF] sm:text-6xl lg:text-7xl">
                <MaskedLine delay={0.1}>{artist.fullName}</MaskedLine>
                <MaskedLine delay={0.24} className="gold-text">
                  Known as {artist.stageName}
                </MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5, ease: EASE }}
                className="font-accent mt-6 max-w-xl text-xl italic leading-relaxed text-[#C8C2B7] sm:text-2xl"
              >
                {artist.tagline}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6, ease: EASE }}
                className="mt-6 flex flex-wrap gap-2"
              >
                {artist.roles.map((role) => (
                  <span
                    key={role}
                    className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#F3E5AB]"
                  >
                    {role}
                  </span>
                ))}
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.72, ease: EASE }}
                className="mt-8 max-w-xl text-base leading-[1.85] text-[#C8C2B7] sm:text-lg"
              >
                {artist.intro}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.84, ease: EASE }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <BookButton location="artist_page" size="lg" testId="artist-hero-book-tickets-btn" />
                <a
                  href={withUtm(artist.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="artist-hero-official-site-link"
                  onClick={() => {
                    trackEvent("performer_website_click", { performer: artist.fullName, cta_location: "artist_hero" });
                    trackOutbound("performer-website", { performer: artist.fullName });
                  }}
                  className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-9 text-sm font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-base"
                >
                  {artist.websiteLabel}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </motion.div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.3, ease: EASE }}
                className="relative mx-auto max-w-[380px]"
              >
                <div className="arch-frame relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/35 bg-[#0E0C0A]">
                  {artist.hasPhoto && artist.photo && (
                    <img
                      src={artist.photo}
                      alt={`${artist.fullName} — portrait`}
                      data-testid="artist-hero-photo"
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="eager"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                  <div className="pointer-events-none absolute inset-0 -z-[1] flex items-center justify-center bg-[#0E0C0A]">
                    <StarPattern id="artist-hero-pattern" opacity={0.32} />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_22%,rgba(212,175,55,0.22),transparent_65%)]" />
                    <p
                      dir="rtl"
                      lang="ur"
                      className="font-urdu relative text-6xl leading-relaxed text-[#E6CA65]"
                      style={{ textShadow: "0 0 44px rgba(212,175,55,0.45)" }}
                    >
                      {artist.urdu}
                    </p>
                  </div>
                </div>
                <div
                  className="arch-frame absolute -left-4 top-4 -z-10 h-full w-full border border-[#D4AF37]/15"
                  aria-hidden="true"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* His Own Words — a direct quote from his official website's About page */}
        {artist.quote && (
          <section data-testid="artist-quote-section" className="relative px-5 py-14 sm:px-8 sm:py-20">
            <div className="relative mx-auto max-w-3xl text-center">
              <Reveal>
                <StarOrnament className="mx-auto h-7 w-7 text-[#D4AF37]/70" />
                <p className="font-accent mt-6 text-xl italic leading-relaxed text-[#F3E5AB] sm:text-2xl lg:text-3xl">
                  “{artist.quote}”
                </p>
                <p className="mt-5 text-xs font-bold uppercase tracking-[0.24em] text-[#918A7E]">
                  — {artist.fullName}
                </p>
              </Reveal>
            </div>
          </section>
        )}

        {/* By the Numbers — hard, first-party-verifiable figures only */}
        {artist.stats?.length > 0 && (
          <section data-testid="artist-stats-section" className="relative px-5 sm:px-8">
            <div className="mx-auto grid max-w-4xl grid-cols-1 divide-y divide-[#D4AF37]/15 overflow-hidden rounded-3xl border border-[#D4AF37]/20 bg-[#0E0C0A] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              {artist.stats.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08}>
                  <div className="flex flex-col items-center gap-1 px-6 py-8 text-center">
                    <span className="font-display gold-text text-3xl sm:text-4xl">{s.value}</span>
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#918A7E]">
                      {s.label}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* Career Highlights */}
        <section data-testid="artist-highlights-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading number="01" title="Career Highlights" urdu="نمایاں کارنامے" />
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {artist.highlights.map((h, i) => (
                <Reveal key={h.title} delay={i * 0.08}>
                  <div className="h-full rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-7">
                    <StarOrnament className="h-6 w-6 text-[#D4AF37]/70" />
                    <h3 className="font-display mt-4 text-xl text-[#F7F4EF]">{h.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#C8C2B7] sm:text-base">{h.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Screen & Studio */}
        <section data-testid="artist-films-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading number="02" title="Screen &amp; Studio" urdu="فلم و پروڈکشن" icon={Clapperboard} />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">
                {artist.films.intro}
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {artist.films.items.map((f, i) => (
                <Reveal key={f.title} delay={0.12 + i * 0.08}>
                  <div className="flex h-full flex-col rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-7">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-2xl text-[#F7F4EF]">{f.title}</h3>
                      {f.upcoming && (
                        <span className="shrink-0 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#F3E5AB]">
                          Upcoming
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{f.role}</p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#C8C2B7] sm:text-base">{f.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <div className="mt-8 rounded-3xl border border-dashed border-[#D4AF37]/25 p-7">
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">Beyond Acting</p>
                <ul className="mt-4 space-y-3">
                  {artist.films.ventures.map((v) => (
                    <li key={v} className="flex items-start gap-3 text-sm leading-relaxed text-[#C8C2B7] sm:text-base">
                      <StarOrnament className="mt-1 h-3.5 w-3.5 shrink-0 text-[#D4AF37]" />
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Books & Writing */}
        <section data-testid="artist-books-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading number="03" title="Books &amp; Writing" urdu="کتابیں و تحریریں" icon={BookOpen} />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">
                {artist.books.intro}
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {artist.books.items.map((title, i) => (
                <Reveal key={title} delay={0.1 + i * 0.05}>
                  <div className="flex h-full items-start gap-3 rounded-2xl border border-[#D4AF37]/20 bg-[#161310] px-5 py-4">
                    <span className="font-display mt-0.5 text-[#D4AF37]/70">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-accent text-base italic leading-snug text-[#F3E5AB] sm:text-lg">
                      {title}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Music & Voice */}
        <section data-testid="artist-music-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading number="04" title="Music &amp; Voice" urdu="موسیقی و آواز" icon={Music2} />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">
                {artist.music.intro}
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {artist.music.items.map((m, i) => (
                <Reveal key={m.title} delay={0.12 + i * 0.08}>
                  <div className="h-full rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-7">
                    <h3 className="font-display text-2xl text-[#F7F4EF]">{m.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#C8C2B7] sm:text-base">{m.note}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.28}>
                <div className="flex h-full items-center rounded-3xl border border-dashed border-[#D4AF37]/25 p-7">
                  <p className="font-accent text-lg italic leading-relaxed text-[#C8C2B7] sm:text-xl">{artist.music.note}</p>
                </div>
              </Reveal>
            </div>

            {/* Music & Videos — verified live from his official YouTube channel */}
            {artist.videos?.items?.length > 0 && (
              <div className="mt-16">
                <Reveal delay={0.1}>
                  <div className="flex flex-wrap items-center gap-3">
                    <Youtube className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                    <h3 className="font-display text-xl text-[#F7F4EF] sm:text-2xl">
                      From {artist.videos.channelName}
                    </h3>
                    <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#F3E5AB]">
                      {artist.videos.subscribersLabel} Subscribers
                    </span>
                  </div>
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#C8C2B7] sm:text-base">
                    {artist.videos.intro}
                  </p>
                </Reveal>
                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {artist.videos.items.map((v, i) => (
                    <Reveal key={v.id} delay={0.12 + i * 0.08}>
                      <div className="overflow-hidden rounded-3xl border border-[#D4AF37]/20 bg-[#161310]">
                        <div className="relative aspect-video w-full bg-black">
                          {playingVideoId === v.id ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${v.id}?autoplay=1`}
                              title={v.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="absolute inset-0 h-full w-full"
                            />
                          ) : (
                            <button
                              type="button"
                              data-testid={`artist-video-play-${v.id}`}
                              onClick={() => {
                                setPlayingVideoId(v.id);
                                trackEvent("performer_video_play", { performer: artist.fullName, video_title: v.title });
                              }}
                              className="group absolute inset-0 h-full w-full"
                              aria-label={`Play ${v.title}`}
                            >
                              <img
                                src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                                alt={`${v.title} — video thumbnail`}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/15" />
                              <span className="absolute inset-0 flex items-center justify-center">
                                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/60 bg-[#0E0C0A]/80 backdrop-blur-sm transition-transform group-hover:scale-110">
                                  <Play className="ml-0.5 h-5 w-5 fill-[#E6CA65] text-[#E6CA65]" aria-hidden="true" />
                                </span>
                              </span>
                            </button>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-3 p-5">
                          <div>
                            <h4 className="font-display text-base text-[#F7F4EF] sm:text-lg">{v.title}</h4>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#D4AF37]">
                              {v.language}
                            </p>
                          </div>
                          <span className="shrink-0 text-xs text-[#918A7E]">{v.viewsLabel}</span>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <Reveal delay={0.35}>
                  <a
                    href={withUtm(artist.videos.channelUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="artist-youtube-channel-link"
                    onClick={() => {
                      trackEvent("performer_youtube_click", { performer: artist.fullName, cta_location: "artist_music_section" });
                      trackOutbound("performer-youtube", { performer: artist.fullName });
                    }}
                    className="mt-8 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#918A7E] underline decoration-[#D4AF37]/30 underline-offset-4 transition-colors hover:text-[#F3E5AB]"
                  >
                    Watch More on His YouTube Channel
                    <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </Reveal>
              </div>
            )}
          </div>
        </section>

        {/* In the Company of Legends */}
        <section data-testid="artist-legends-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              number="05"
              title="In the Company of Legends"
              urdu="عظیم شخصیات کی صحبت میں"
              icon={Users}
            />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">
                {artist.legends.intro}
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {artist.legends.items.map((p, i) => (
                <Reveal key={p.name} delay={0.1 + i * 0.05}>
                  <div
                    data-testid={`legend-card-${i + 1}`}
                    className="arch-frame group relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/25 bg-[#161310] transition-colors duration-500 hover:border-[#D4AF37]/55"
                  >
                    {p.photo ? (
                      <>
                        <img
                          src={p.photo}
                          alt={p.name}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                        <div
                          className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(8,7,5,0)_38%,rgba(8,7,5,0.55)_70%,rgba(8,7,5,0.92)_100%)]"
                          aria-hidden="true"
                        />
                      </>
                    ) : (
                      <>
                        <StarPattern id={`legend-pattern-${i}`} opacity={0.22} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <StarOrnament className="h-7 w-7 text-[#D4AF37]/45" />
                        </div>
                        <div
                          className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-[linear-gradient(180deg,transparent_0%,rgba(8,7,5,0.9)_100%)]"
                          aria-hidden="true"
                        />
                      </>
                    )}
                    <div className="pointer-events-none absolute inset-[6px] border border-[#F3E5AB]/10" aria-hidden="true" />
                    <div className="absolute inset-x-0 bottom-0 px-2.5 pb-3.5 pt-2 text-center sm:px-3.5 sm:pb-5">
                      <span className="font-display block text-[13px] leading-snug text-[#F7F4EF] sm:text-base">
                        {p.name}
                      </span>
                      {p.note && (
                        <span className="mt-1 block text-[10px] leading-snug text-[#C8C2B7] sm:text-xs">
                          {p.note}
                        </span>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery — populated once real photos are supplied; renders nothing until then */}
        {artist.gallery?.items?.length > 0 && (
          <section data-testid="artist-gallery-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
            <div className="mx-auto max-w-6xl">
              <Reveal>
                <h2 className="font-display text-center text-3xl leading-tight tracking-tight text-[#F7F4EF] sm:text-4xl">
                  On Stage &amp; Behind the Scenes
                </h2>
                {artist.gallery.intro && (
                  <p className="mx-auto mt-4 max-w-2xl text-center text-base leading-[1.8] text-[#C8C2B7]">
                    {artist.gallery.intro}
                  </p>
                )}
              </Reveal>
              <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {artist.gallery.items.map((g, i) => (
                  <Reveal key={g.src} delay={i * 0.05}>
                    <div className="arch-frame aspect-[3/4] overflow-hidden border border-[#D4AF37]/20 bg-[#0E0C0A]">
                      <img
                        src={g.src}
                        alt={g.caption || `${artist.fullName} — photo ${i + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Timeline */}
        <section data-testid="artist-timeline-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-4xl">
            <SectionHeading number="06" title="The Journey" urdu="سفر" />
            <div className="mt-14 space-y-0">
              {artist.timeline.map((t, i) => (
                <Reveal key={t.year} delay={i * 0.1}>
                  <div className="relative flex gap-6 pb-10 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#161310] font-display text-sm text-[#F3E5AB]">
                        {t.year}
                      </span>
                      {i < artist.timeline.length - 1 && (
                        <span className="mt-2 w-px flex-1 bg-gradient-to-b from-[#D4AF37]/40 to-transparent" aria-hidden="true" />
                      )}
                    </div>
                    <div className="pt-2">
                      <h3 className="font-display text-xl text-[#F7F4EF] sm:text-2xl">{t.title}</h3>
                      <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#C8C2B7] sm:text-base">{t.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Connection to Chitti Ayee Hai */}
        <section data-testid="artist-connection-section" className="relative overflow-hidden px-5 py-20 sm:px-8 sm:py-28">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute left-1/2 top-1/2 h-[500px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/[0.06] blur-[120px]" />
          </div>
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <StarOrnament className="mx-auto h-8 w-8 text-[#D4AF37]/70" />
              <p className="font-accent mt-6 text-2xl italic leading-relaxed text-[#F3E5AB] sm:text-3xl lg:text-4xl">
                {artist.connection}
              </p>
            </Reveal>
          </div>
        </section>

        {/* Final CTA */}
        <section data-testid="artist-final-cta-section" className="relative px-5 pb-24 sm:px-8">
          <Reveal>
            <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-3xl border border-[#D4AF37]/30 bg-[#0E0C0A] px-8 py-12 text-center">
              <h2 className="font-display text-2xl text-[#F7F4EF] sm:text-3xl">
                Experience it live at {SITE.eventName}
              </h2>
              <p className="max-w-xl text-sm leading-relaxed text-[#918A7E] sm:text-base">
                {SITE.date} · {SITE.venue.name}, {SITE.venue.city}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <BookButton location="artist_final_cta" testId="artist-final-book-tickets-btn" />
                <a
                  href="/sponsor"
                  data-testid="artist-final-sponsor-btn"
                  onClick={() => trackEvent("sponsor_page_click", { cta_location: "artist_final_cta" })}
                  className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-9 text-sm font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-base"
                >
                  Become a Sponsor
                </a>
                <a
                  href={`https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(
                    `Hi! I'm interested in booking a stall at ${SITE.eventName}.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="artist-final-stall-btn"
                  onClick={() => {
                    trackEvent("stall_enquiry_click", { cta_location: "artist_final_cta" });
                    trackOutbound("whatsapp-stall-enquiry");
                  }}
                  className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-9 text-sm font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-base"
                >
                  Book a Stall
                </a>
              </div>
              <a
                href={withUtm(artist.website)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="artist-final-official-site-link"
                onClick={() => {
                  trackEvent("performer_website_click", { performer: artist.fullName, cta_location: "artist_final_cta" });
                  trackOutbound("performer-website", { performer: artist.fullName });
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#918A7E] underline decoration-[#D4AF37]/30 underline-offset-4 transition-colors hover:text-[#F3E5AB]"
              >
                {artist.websiteLabel}
                <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-[#D4AF37]/15 px-5 py-8 text-center text-sm text-[#918A7E]">
        © {new Date().getFullYear()} {SITE.eventName} · Event by {SITE.organizer} ·{" "}
        <a
          href={`mailto:${SITE.contactEmail}`}
          className="text-[#F3E5AB] underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#E6CA65]"
        >
          {SITE.contactEmail}
        </a>
      </footer>
      <StickyCta />
    </div>
  );
}
